import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../lib/api';
import i18n from '../i18n';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('safepro_token');
    if (token) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, []);

  async function fetchProfile(langOverride = null) {
    try {
      const { data } = await api.get('/api/admin/me');
      const lang = langOverride || data?.language || 'fi';
      localStorage.setItem('safepro_language', lang);
      await i18n.changeLanguage(lang);
      setProfile(data);
      setUser({ id: data.id, email: data.email });
      return lang;
    } catch {
      localStorage.removeItem('safepro_token');
      localStorage.removeItem('safepro_user');
      setUser(null);
      setProfile(null);
      return null;
    } finally {
      setLoading(false);
    }
  }

  async function signIn(email, password) {
    const preLoginLang = localStorage.getItem('safepro_language');
    const res = await api.post('/api/auth/login', { email, password });
    const { token, user: userData } = res.data;
    localStorage.setItem('safepro_token', token);
    localStorage.setItem('safepro_user', JSON.stringify(userData));
    if (preLoginLang) {
      try {
        await api.patch('/api/admin/me/language', { language: preLoginLang });
      } catch {
        // Kielivalinnan tallennus epäonnistui — käytetään silti valittua kieltä tässä sessiossa
      }
    }
    await fetchProfile(preLoginLang || null);
  }

  // SSO (#28): backend ohjaa Microsoft-kirjautumisen jälkeen takaisin
  // kirjautumissivulle omalla JWT:llä (URL-fragmentissa), ks. LoginPage.
  async function signInWithToken(token) {
    localStorage.setItem('safepro_token', token);
    const lang = await fetchProfile();
    if (!lang) throw new Error('sso_profile_failed');
  }

  async function signOut() {
    localStorage.removeItem('safepro_token');
    localStorage.removeItem('safepro_user');
    setUser(null);
    setProfile(null);
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signInWithToken, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
